import { useState, useEffect, useCallback } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, Plus, Truck, Camera, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODES_PAIEMENT = [
  { value: 'Chèque', label: 'Chèque' },
  { value: 'Virement', label: 'Virement' },
  { value: 'Espèces', label: 'Espèces' },
  { value: 'CB', label: 'CB' },
  { value: 'PayPal', label: 'PayPal' }
];

const DepenseModal = ({ open, onClose, onSuccess, editData, defaultMonth, defaultYear }) => {
  const { createDepense, updateDepense } = useTransactions();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // OCR State
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrPreview, setOcrPreview] = useState(null);
  
  // CRM integration for fournisseurs
  const [fournisseurs, setFournisseurs] = useState([]);
  const [fournisseursLoading, setFournisseursLoading] = useState(false);
  const [fournisseurPopoverOpen, setFournisseurPopoverOpen] = useState(false);
  const [showNewFournisseurForm, setShowNewFournisseurForm] = useState(false);
  const [newFournisseurData, setNewFournisseurData] = useState({ nom: '' });
  const [creatingFournisseur, setCreatingFournisseur] = useState(false);
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    date: getTodayDate(),
    reference: '',
    fournisseur: '',
    fournisseur_id: null,
    nature: '',
    montant: '',
    mode_paiement: 'CB'
  });

  // Fetch fournisseurs from CRM
  const fetchFournisseurs = useCallback(async () => {
    if (!token) return;
    setFournisseursLoading(true);
    try {
      const response = await axios.get(API_URL + '/api/crm/clients?type_client=fournisseur', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setFournisseurs(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching fournisseurs:', error);
    } finally {
      setFournisseursLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchFournisseurs();
    }
  }, [open, fetchFournisseurs]);

  useEffect(() => {
    if (editData) {
      setFormData({
        date: editData.date,
        reference: editData.reference,
        fournisseur: editData.fournisseur,
        fournisseur_id: editData.fournisseur_id || null,
        nature: editData.nature,
        montant: String(editData.montant),
        mode_paiement: editData.mode_paiement
      });
    } else if (open) {
      setFormData({
        date: getTodayDate(),
        reference: '',
        fournisseur: '',
        fournisseur_id: null,
        nature: '',
        montant: '',
        mode_paiement: 'CB'
      });
    }
    setErrors({});
    setShowNewFournisseurForm(false);
    setOcrPreview(null);
  }, [editData, open]);

  // OCR function to process ticket image
  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG ou WebP');
      return;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setOcrPreview(ev.target?.result);
    };
    reader.readAsDataURL(file);
    
    setOcrLoading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        API_URL + '/api/ocr/ticket',
        formDataUpload,
        {
          headers: { 
            Authorization: 'Bearer ' + token,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success && response.data.data) {
        const ocrData = response.data.data;
        
        // Pre-fill form with OCR data
        setFormData(prev => ({
          ...prev,
          date: ocrData.date || prev.date,
          fournisseur: ocrData.fournisseur || prev.fournisseur,
          nature: ocrData.nature || prev.nature,
          montant: ocrData.montant ? String(ocrData.montant) : prev.montant,
          reference: ocrData.reference || prev.reference,
          mode_paiement: ocrData.mode_paiement || prev.mode_paiement
        }));
        
        const confidence = Math.round((ocrData.confiance || 0.8) * 100);
        toast.success(`Ticket analysé (confiance: ${confidence}%). Vérifiez les données.`);
      } else {
        toast.warning(response.data.message || 'Analyse partielle. Complétez manuellement.');
      }
    } catch (error) {
      console.error('Erreur OCR:', error);
      toast.error('Erreur lors de l\'analyse du ticket');
    } finally {
      setOcrLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) {
      newErrors.date = 'La date est obligatoire';
    }
    if (!formData.reference.trim()) {
      newErrors.reference = 'La référence est obligatoire';
    }
    if (!formData.fournisseur.trim()) {
      newErrors.fournisseur = 'Le fournisseur est obligatoire';
    }
    if (!formData.nature.trim()) {
      newErrors.nature = 'La nature est obligatoire';
    }
    if (!formData.montant) {
      newErrors.montant = 'Le montant est obligatoire';
    } else {
      const montant = parseFloat(formData.montant);
      if (isNaN(montant) || montant <= 0) {
        newErrors.montant = 'Le montant doit être supérieur à 0';
      }
    }
    if (!formData.mode_paiement) {
      newErrors.mode_paiement = 'Le mode de paiement est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectFournisseur = (fournisseur) => {
    setFormData({ 
      ...formData, 
      fournisseur: fournisseur.nom,
      fournisseur_id: fournisseur.id
    });
    setFournisseurPopoverOpen(false);
  };

  const handleCreateFournisseur = async () => {
    if (!newFournisseurData.nom.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }

    setCreatingFournisseur(true);
    try {
      const response = await axios.post(API_URL + '/api/crm/clients', {
        nom: newFournisseurData.nom,
        type_client: 'fournisseur'
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      const newFournisseur = response.data;
      setFournisseurs([...fournisseurs, newFournisseur]);
      setFormData({ 
        ...formData, 
        fournisseur: newFournisseur.nom,
        fournisseur_id: newFournisseur.id
      });
      setShowNewFournisseurForm(false);
      setNewFournisseurData({ nom: '' });
      setFournisseurPopoverOpen(false);
      toast.success('Fournisseur créé et sélectionné');
    } catch (error) {
      toast.error('Erreur lors de la création du fournisseur');
    } finally {
      setCreatingFournisseur(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const data = {
        ...formData,
        montant: parseFloat(formData.montant)
      };

      if (editData) {
        await updateDepense(editData.id, data);
        toast.success('✓ Dépense modifiée');
      } else {
        await createDepense(data);
        toast.success('✓ Dépense enregistrée');
      }
      
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="depense-modal">
        <DialogHeader>
          <DialogTitle className="font-chivo text-xl">
            {editData ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {editData ? 'Modifiez les informations de la dépense' : 'Ajoutez une nouvelle dépense à votre registre'}
          </DialogDescription>
        </DialogHeader>

        {/* OCR Section - Scanner un ticket */}
        {!editData && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-purple-900">Scanner un ticket</p>
                <p className="text-xs text-purple-600">Photographiez votre ticket pour un remplissage automatique</p>
              </div>
              <div>
                <input
                  type="file"
                  id="ocr-upload"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleOCRUpload}
                  className="hidden"
                  disabled={ocrLoading}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="cursor-pointer border-purple-300 text-purple-700 hover:bg-purple-100"
                  disabled={ocrLoading}
                  onClick={() => document.getElementById('ocr-upload').click()}
                >
                  {ocrLoading ? (
                    <span className="flex items-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyse...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Camera className="h-4 w-4 mr-2" />
                      Scanner
                    </span>
                  )}
                </Button>
              </div>
            </div>
            {ocrPreview && (
              <div className="mt-3 flex items-center gap-2">
                <img 
                  src={ocrPreview} 
                  alt="Aperçu ticket" 
                  className="h-12 w-12 object-cover rounded border"
                />
                <span className="text-xs text-purple-600">Ticket analysé</span>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={errors.date ? 'border-red-500' : ''}
                data-testid="depense-date-input"
              />
              {errors.date && <p className="text-xs text-red-500">{errors.date}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference" className="text-sm font-medium">
                Référence <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reference"
                type="text"
                placeholder="FACT-001"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className={errors.reference ? 'border-red-500' : ''}
                data-testid="depense-reference-input"
              />
              {errors.reference && <p className="text-xs text-red-500">{errors.reference}</p>}
            </div>
          </div>

          {/* Fournisseur avec CRM */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Fournisseur <span className="text-red-500">*</span>
            </Label>
            <Popover open={fournisseurPopoverOpen} onOpenChange={setFournisseurPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={fournisseurPopoverOpen}
                  className={cn(
                    "w-full justify-between font-normal",
                    errors.fournisseur ? 'border-red-500' : '',
                    !formData.fournisseur && "text-muted-foreground"
                  )}
                  data-testid="depense-fournisseur-select"
                >
                  {formData.fournisseur || "Sélectionner ou créer un fournisseur..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                {!showNewFournisseurForm ? (
                  <Command>
                    <CommandInput placeholder="Rechercher un fournisseur..." />
                    <CommandList>
                      <CommandEmpty>
                        <p className="text-sm text-muted-foreground py-2">Aucun fournisseur trouvé</p>
                      </CommandEmpty>
                      {fournisseurs.length > 0 && (
                        <CommandGroup heading="Fournisseurs existants">
                          {fournisseurs.map((fournisseur) => (
                            <CommandItem
                              key={fournisseur.id}
                              value={fournisseur.nom}
                              onSelect={() => handleSelectFournisseur(fournisseur)}
                              className="cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.fournisseur_id === fournisseur.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex items-center gap-2 flex-1">
                                <Truck className="h-4 w-4 text-orange-500" />
                                <span>{fournisseur.nom}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => setShowNewFournisseurForm(true)}
                          className="cursor-pointer text-emerald-600"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Créer un nouveau fournisseur
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                ) : (
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Nouveau fournisseur</h4>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowNewFournisseurForm(false)}
                      >
                        Retour
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Nom du fournisseur *</Label>
                        <Input
                          placeholder="Nom ou raison sociale"
                          value={newFournisseurData.nom}
                          onChange={(e) => setNewFournisseurData({ ...newFournisseurData, nom: e.target.value })}
                          autoFocus
                        />
                      </div>
                      <Button 
                        className="w-full" 
                        onClick={handleCreateFournisseur}
                        disabled={creatingFournisseur}
                      >
                        {creatingFournisseur ? 'Création...' : 'Créer et sélectionner'}
                      </Button>
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {errors.fournisseur && <p className="text-xs text-red-500">{errors.fournisseur}</p>}
            {formData.fournisseur && !formData.fournisseur_id && (
              <p className="text-xs text-amber-600">
                💡 Ce fournisseur n&apos;est pas dans votre CRM. Créez-le pour un meilleur suivi.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="nature" className="text-sm font-medium">
              Nature / Description <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nature"
              type="text"
              placeholder="Fournitures, abonnement, matériel..."
              value={formData.nature}
              onChange={(e) => setFormData({ ...formData, nature: e.target.value })}
              className={errors.nature ? 'border-red-500' : ''}
              data-testid="depense-nature-input"
            />
            {errors.nature && <p className="text-xs text-red-500">{errors.nature}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="montant" className="text-sm font-medium">
                Montant (€) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="montant"
                type="number"
                step="0.01"
                min="0"
                placeholder="150.00"
                value={formData.montant}
                onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                className={errors.montant ? 'border-red-500' : ''}
                data-testid="depense-montant-input"
              />
              {errors.montant && <p className="text-xs text-red-500">{errors.montant}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mode" className="text-sm font-medium">
                Mode de paiement <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.mode_paiement} 
                onValueChange={(value) => setFormData({ ...formData, mode_paiement: value })}
              >
                <SelectTrigger 
                  className={errors.mode_paiement ? 'border-red-500' : ''}
                  data-testid="depense-mode-select"
                >
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {MODES_PAIEMENT.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.mode_paiement && <p className="text-xs text-red-500">{errors.mode_paiement}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="depense-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
              data-testid="depense-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Enregistrement...
                </span>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DepenseModal;
